// Centrální registr route kontraktů – composition bod pro src/routes
// Skládá veřejné route kontrakty slice bez přímých importů internals

import type { TFunction } from 'i18next';

import type { SliceRouteContract } from '@/shared/lib/vsa';
import type { MainMenuItem } from '@/app/layout/mainMenuItems';
import { getRuntimeConfig } from '@/shared/runtimeConfig';

import { notFoundRouteContract } from '@/modules/not-found/routeContract';
import {
  plannerRouteContract,
  plannerCalendarRouteContract,
} from '@/modules/planner/routeContract';
import { PLANNER_PERMISSION } from '@/modules/planner/permissions';
import { columbusRouteContract } from '@/modules/columbus-planner/routeContract';
import { COLUMBUS_PERMISSION } from '@/modules/columbus-planner/permissions';
import { designShowcaseRouteContract } from '@/modules/design-showcase';
import { legacyFormRouteContract } from '@/modules/legacy-form/routeContract';
import { securityRouteContract } from '@/modules/security/routeContract';
import { SECURITY_ROLE_PERMISSIONS } from '@/modules/security/permissions';
import { reservationsRouteContract } from '@/modules/reservations/routeContract';
import { RESERVATIONS_BUSINESS_CASES } from '@/modules/reservations/permissions';
import {
  productsRouteContract,
  productsChainsRouteContract,
} from '@/modules/products/routeContract';
import { PRODUCTS_STATIONS, PRODUCTS_STATION_CHAINS } from '@/modules/products/permissions';
import { standardMenuItem } from '@/app/layout/mainMenuItems';

export const routeContracts = {
  notFound: notFoundRouteContract,
  planner: plannerRouteContract,
  plannerCalendar: plannerCalendarRouteContract,
  columbusPlan: columbusRouteContract,
  designShowcase: designShowcaseRouteContract,
  legacyForm: legacyFormRouteContract,
  security: securityRouteContract,
  reservations: reservationsRouteContract,
  products: productsRouteContract,
  productsChains: productsChainsRouteContract,
} as const satisfies Record<string, SliceRouteContract>;

/**
 * Baseline (native) položky hlavního menu.
 *
 * Položky s `section` se mergují do stejnojmenné root sekce (Phoenix native ↔ legacy menu);
 * ve fázi 1 se vkládají na začátek sekce. Položky bez `section` zůstávají samostatným
 * tlačítkem vlevo v liště.
 */
export function getMainMenuItems(t: TFunction): readonly MainMenuItem[] {
  // `section` slouží jako matching klíč proti legacy Magic sekcím. Magic si sekce lokalizuje
  // dle kultury (CZ „Produkty", EN „Products"), takže i náš klíč musí být lokalizovaný a shodovat
  // se s Magic labelem v daném jazyce — jinak by se native položky nesloučily a vznikla by
  // duplicitní sekce. Stejný text se použije i jako zobrazený popisek v čistě-Phoenix režimu.
  const productsSection = t('menu.sections.products');
  const items: MainMenuItem[] = [
    standardMenuItem('planner', t('menu.planner'), '/planner', 20, {
      section: productsSection,
      permission: PLANNER_PERMISSION,
    }),
    standardMenuItem('planner-calendar', t('menu.plannerCalendar'), '/planner-calendar', 25, {
      section: productsSection,
      permission: PLANNER_PERMISSION,
    }),
    standardMenuItem('columbus-planner', t('menu.columbusPlanner'), '/columbus-planner', 30, {
      section: productsSection,
      permission: COLUMBUS_PERMISSION,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — agenda Stanice (merge do stejnojmenné legacy skupiny).
    standardMenuItem('stations', t('menu.stations'), '/products', 35, {
      section: t('menu.sections.inventory'),
      group: t('menu.groups.codebooks'),
      permission: PRODUCTS_STATIONS,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — agenda Trasy autobusů.
    standardMenuItem('station-chains', t('menu.stationChains'), '/products-chains', 36, {
      section: t('menu.sections.inventory'),
      group: t('menu.groups.codebooks'),
      permission: PRODUCTS_STATION_CHAINS,
    }),
    // Sekce „Rezervace" — merguje se se stejnojmennou legacy Magic sekcí.
    standardMenuItem('reservations', t('menu.reservations'), '/reservations', 40, {
      section: t('menu.sections.reservations'),
      permission: RESERVATIONS_BUSINESS_CASES,
    }),
    // Sekce „Systém" — pokud má Magic stejnojmennou sekci, položka se do ní mergne;
    // jinak vznikne samostatná Phoenix sekce.
    standardMenuItem('security', t('menu.security'), '/security', 90, {
      section: t('menu.sections.system'),
      permission: SECURITY_ROLE_PERMISSIONS,
    }),
  ];

  // Design showcase je vývojářský nástroj — v menu jen na dev prostředí.
  if (getRuntimeConfig().app.environment === 'dev') {
    items.push(
      standardMenuItem('design', t('menu.design'), '/design', 40, {
        section: t('menu.sections.general'),
      })
    );
  }

  return items;
}
